import { useCallback, useEffect, useRef, useState } from 'react';
import { bulkUpdatePrices } from '../api';
import { applicableRungs } from '../utils/lionAutoLadder';
import { writeLadderChunks } from '../utils/lionAutoWrite';

// Lion Auto's price write: a calculated ladder's OK rungs through bulk-price
// (utils/lionAutoWrite), one write at a time, never retried nor re-sent.
// Holds the last write's outcome for the ladder bar's sticky banner until the
// page resets it (the next Calculate, another group):
//   { id, scope, status, total, applied, failed, pending, message, retryAt, locked }
// status: idle | writing | done | failed | rate_limited | unknown; scope:
// what the outcome belongs to (the page shows it only there). locked: a 429
// whose countdown still runs (released here at retryAt), or a timed-out
// write whose fresh price read has not landed (the page calls
// unlockUnknown); while locked nothing may write again (writeBlockReason)
// and a plain reset keeps the outcome. onApplied({ results, errors }) gets
// the rows that landed and the per-row refusals (the page patches its
// caches, pins the L, toasts); onOutcomeUnknown() runs after a timed-out
// chunk (the page reads fresh prices).
const IDLE = Object.freeze({
  id: 0, scope: null, status: 'idle', total: 0, applied: [], failed: [], pending: [], message: null, retryAt: null, locked: false,
});

const UNEXPECTED_TEXT = 'The price write stopped unexpectedly: some prices may have been written. '
  + 'Refresh comps and check them before you Calculate again.';

// A page callback that throws must not turn the write's outcome into a rejection
function safely(fn) {
  try {
    fn();
  } catch (err) {
    console.error('[Lion Auto] ladder write callback', err);
  }
}

export function useLadderWrite({ onApplied, onOutcomeUnknown } = {}) {
  const [state, setState] = useState(IDLE);
  const callbacksRef = useRef({ onApplied, onOutcomeUnknown });
  callbacksRef.current = { onApplied, onOutcomeUnknown };
  // Set the moment a write starts, so a caller in the same render can't start a second
  const busyRef = useRef(false);
  const seqRef = useRef(0);
  // Bumped by a forced reset (another event): a write still running then lands as idle
  const genRef = useRef(0);

  // rungs: the answer's rungs (only OK ones are sent). Resolves with the
  // outcome, or null when another write is still running; never rejects.
  const run = useCallback(async (rungs, { scope = null } = {}) => {
    if (busyRef.current) return null;
    busyRef.current = true;
    const id = ++seqRef.current;
    const gen = genRef.current;
    const total = applicableRungs(rungs).length;
    setState({ ...IDLE, id, scope, status: 'writing', total });
    let result;
    try {
      result = await writeLadderChunks(rungs, bulkUpdatePrices);
    } catch (err) {
      // writeLadderChunks answers every request itself; a throw here is a bug
      // mid-run, so what landed is unknown: shown, and the page re-reads prices
      result = { status: 'unknown', applied: [], failed: [], pending: [], message: `${UNEXPECTED_TEXT} (${err?.message || 'unknown error'})`, retryAt: null };
    } finally {
      busyRef.current = false;
    }
    const locked = result.status === 'unknown' || result.status === 'rate_limited';
    setState(genRef.current === gen ? { id, scope, total, ...result, locked } : IDLE);
    const errors = result.failed.filter(f => f.kind === 'refused');
    if (result.applied.length || errors.length) safely(() => callbacksRef.current.onApplied?.({ results: result.applied, errors }));
    if (result.status === 'unknown') safely(() => callbacksRef.current.onOutcomeUnknown?.());
    return result;
  }, []);

  // A 429's lock goes when its countdown ends
  const { id, status, retryAt, locked } = state;
  useEffect(() => {
    if (!locked || status !== 'rate_limited') return undefined;
    const t = setTimeout(() => {
      setState(s => (s.id === id && s.locked ? { ...s, locked: false } : s));
    }, Math.max(0, (Number(retryAt) || 0) - Date.now()));
    return () => clearTimeout(t);
  }, [id, status, retryAt, locked]);

  // The timed-out write's fresh price read landed: Calculate may run again
  const unlockUnknown = useCallback(() => {
    setState(s => (s.status === 'unknown' && s.locked ? { ...s, locked: false } : s));
  }, []);

  // Clears the outcome; never while a write runs, and (unless forced, for
  // another event) never while it is locked
  const reset = useCallback(({ force = false } = {}) => {
    if (force) genRef.current += 1;
    setState(s => (s.status === 'writing' || (s.locked && !force) ? s : IDLE));
  }, []);
  const isBusy = useCallback(() => busyRef.current, []);

  return { ...state, writing: state.status === 'writing', run, reset, unlockUnknown, isBusy };
}
